import { useCallback, useEffect, useState } from 'react';
import { orderBy, query } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { RenderIf } from 'react-rainbow-components';
import { showAppNotification } from '@rainbow-modules/app';
import Footer from 'components/Footer';
import ChevronLeft from 'components/icons/chevronLeft';
import Close from 'components/icons/close';
import History from 'components/Instructions/history';
import useDescriptionVersions from 'data/firestore/agent/descriptionversion/useCollection';
import useAgentData from 'hooks/useAgentData';
import useUpdateAgentMutation from 'data/hooks/useUpdateAgentMutation';
import {
    Content,
    Header,
    Title,
    InstructionsInputContainer,
    InstructionsContainer,
    PageContainer,
    HeaderContent,
    StyledButtonIcon,
    Row,
    InstructionsInputContent,
    InstructionsInput,
    HelperContainer,
    HeaderWithActions,
} from './styled';
import FormFooter from './formFooter';
import { HistoryItem } from './types';

const EditAditionalCompanyInfo = () => {
    const { agentId = '' } = useParams();
    const { agent } = useAgentData();
    const [editorContent, setEditorContent] = useState<string>('');
    const navigate = useNavigate();

    const [selectedVersion, setSelectedVersion] = useState<HistoryItem | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);

    const { data: versions } = useDescriptionVersions(agentId as string, {
        disabled: !agentId,
        listQueryFn: (ref) => query(ref, orderBy('createdAt', 'desc')),
    });

    const {
        mutateAsync: updateAgent,
        isLoading: isUpdatingAgent,
    } = useUpdateAgentMutation(agentId as string);

    const historyItems = versions.map<HistoryItem>((v) => ({
        ...v,
        value: v.description,
    }));

    const handleSaveChanges = useCallback(async () => {
        try {
            await updateAgent({ body: { description: editorContent } });
            showAppNotification({
                title: 'Success',
                description: 'Your changes have been saved.',
                icon: 'success',
                timeout: 5000,
            });
        } catch (error: any) {
            const message = (error as any)?.message || 'Something went wrong. Please try again.';
            showAppNotification({
                title: 'Error',
                description: message,
                icon: 'error',
                timeout: 5000,
            });
        }
    }, [editorContent, updateAgent]);

    useEffect(() => {
        setEditorContent(agent?.description || '');
    }, [agent?.description]);

    useEffect(() => {
        if (selectedVersion) {
            setEditorContent(selectedVersion?.value || '');
        }
    }, [selectedVersion]);

    return (
        <PageContainer>
            <Content>
                <InstructionsContainer>
                    <Header>
                        <HeaderContent>
                            <Row>
                                <StyledButtonIcon
                                    variant="base"
                                    borderRadius="semi-square"
                                    icon={<ChevronLeft />}
                                    onClick={() => navigate(-1)}
                                />
                                <Title title="Edit">Company Description</Title>
                            </Row>
                        </HeaderContent>
                    </Header>
                    <InstructionsInputContainer>
                        <InstructionsInputContent>
                            <InstructionsInput
                                value={editorContent}
                                onChange={(value) => setEditorContent(value as string)}
                            />
                            <FormFooter
                                formId="instructions-form"
                                content={editorContent}
                                submitLoading={isUpdatingAgent}
                                hideHistoryAction={historyItems.length === 0}
                                onOpenHistory={() => setHistoryOpen(true)}
                                onsubmit={() => handleSaveChanges()}
                            />
                        </InstructionsInputContent>
                    </InstructionsInputContainer>
                </InstructionsContainer>
                <RenderIf isTrue={historyOpen}>
                    <HelperContainer>
                        <HeaderWithActions>
                            <Title>{historyOpen ? 'History' : 'Help'}</Title>
                            <StyledButtonIcon
                                icon={<Close />}
                                onClick={() => setHistoryOpen(false)}
                                size="small"
                                className="rainbow-m-left_small"
                                borderRadius="semi-square"
                                variant="base"
                            />
                        </HeaderWithActions>
                        <RenderIf isTrue={historyOpen}>
                            <History
                                items={historyItems}
                                selectedItem={selectedVersion || historyItems[0]}
                                onSelectedVersion={setSelectedVersion}
                            />
                        </RenderIf>
                    </HelperContainer>
                </RenderIf>
            </Content>
            <Footer />
        </PageContainer>
    );
};

export default EditAditionalCompanyInfo;
